import { MatchmakingV2MessageType } from '@playcastdotio/Messaging';
import { profileState } from '../../../stores/lobbyStores';
import { joinState } from '../../../stores/globalStores';
import { setPlayerBroadcastId } from '../../playerObservability';

/**
 * Handle inbound v2 matchmaking messages. Updates the same stores as v1,
 * mapping v2 semantics to the existing profileState fields so
 * MatchmakingManager's store subscriptions fire without changes.
 */
export const handleMatchmakingV2Message = (message: { type: string; [key: string]: unknown }): void => {
  const type = message.type as MatchmakingV2MessageType;

  switch (type) {
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_CREATED: {
      const matchRequestId = message.matchRequestId as string;
      console.log('[MatchmakingV2] Match request created:', matchRequestId);
      profileState.updateState({
        ...profileState.currentState,
        selectedInvitationId: matchRequestId,
        creating: false,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_QUEUE_STATUS: {
      const noHostsAvailable = (message.noHostsAvailable as boolean) ?? false;
      const matchRequestId = message.matchRequestId as string;
      const position = message.position as number;
      console.log('[MatchmakingV2] Queue status: position', position, 'noHosts:', noHostsAvailable);
      profileState.updateState({
        ...profileState.currentState,
        noHostsAvailable,
        inQueue: matchRequestId,
        queuePosition: position,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FOUND: {
      const matchRequestId = message.matchRequestId as string;
      const estimatedSetupTimeSeconds = message.estimatedSetupTimeSeconds as number | undefined;
      console.log('[MatchmakingV2] Match found:', matchRequestId);
      profileState.updateState({
        ...profileState.currentState,
        matchFoundId: matchRequestId,
        inQueue: undefined,
        queuePosition: undefined,
        estimatedSetupTimeSeconds,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_READY: {
      const broadcastId = message.broadcastId as string;
      const matchRequestId = message.matchRequestId as string;
      console.log('[MatchmakingV2] Match ready, broadcastId:', broadcastId);
      setPlayerBroadcastId(broadcastId);
      profileState.updateState({
        ...profileState.currentState,
        matchBroadcastId: broadcastId,
        matchFoundId: matchRequestId,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_END: {
      const matchRequestId = message.matchRequestId as string;
      const reason = message.reason as string;
      console.log('[MatchmakingV2] Match ended:', matchRequestId, 'reason:', reason);
      profileState.updateState({
        ...profileState.currentState,
        matchEnded: { matchRequestId, reason },
        matchBroadcastId: undefined,
        matchFoundId: undefined,
        inQueue: undefined,
        queuePosition: undefined,
        estimatedSetupTimeSeconds: undefined,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FAILED: {
      const reason = message.reason as string;
      const canRetry = (message.canRetry as boolean) ?? false;
      console.log('[MatchmakingV2] Match failed:', reason, 'canRetry:', canRetry);
      profileState.updateState({
        ...profileState.currentState,
        noHostsAvailable: true,
        matchFailed: { reason, canRetry },
        inQueue: undefined,
        queuePosition: undefined,
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_ERROR: {
      const error = message.error as { code: string; message: string };
      console.error('[MatchmakingV2] Error:', error?.code, error?.message);
      joinState.updateState({
        ...joinState.currentState,
        state: 'error',
      });
      break;
    }

    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_UPDATED:
    case MatchmakingV2MessageType.MATCHMAKING_V2_STATE_UPDATE:
      break;

    default:
      console.warn('[MatchmakingV2] Unhandled message type:', type);
  }
};
